/**
 * Catálogo de faixas de saúde — versionado, um perfil só, declarado na tela.
 *
 * POR QUE UM PERFIL SÓ (`geral`): o setor da empresa é campo livre hoje e não
 * existe base estatística própria. Publicar cinco perfis setoriais seria ~200
 * números curados que ninguém mantém, e um distribuidor que se marca como
 * varejo destruiria a leitura da própria margem. Enquanto não houver base, a
 * tela diz "faixa geral, não setorial" — que é honesto e barato.
 *
 * POR QUE FAIXA E NÃO SÓ NÚMERO: número financeiro sozinho não responde a
 * pergunta do dono. 1,4 de liquidez é bom ou ruim? A faixa é a resposta, e ela
 * carrega a fonte junto para o consultor poder discordar com argumento.
 *
 * REGRA QUE MANDA AQUI: valor ausente NUNCA cai numa faixa. `null` não é baixo,
 * é desconhecido — e classificar desconhecido como "crítico" já aconteceu neste
 * produto (a tabela resumo do IndicadoresTab, achado da onda 3).
 */

export const VERSAO_DAS_FAIXAS = 'v1';

/** Enquanto não houver base setorial própria, a régua é uma só e é dita na tela. */
export const PERFIL_DE_FAIXAS = {
  id: 'geral',
  nome: 'Faixa geral',
  aviso: 'Faixa geral de referência, não setorial'
};

/** Os quatro degraus, mais os dois estados que não são degrau nenhum. */
export const NIVEIS = {
  excelente: { rotulo: 'Excelente', cor: 'success', ordem: 4 },
  bom: { rotulo: 'Bom', cor: 'success', ordem: 3 },
  atencao: { rotulo: 'Atenção', cor: 'warning', ordem: 2 },
  critico: { rotulo: 'Crítico', cor: 'error', ordem: 1 },
  ausente: { rotulo: 'Não informado', cor: 'default', ordem: 0 },
  semFaixa: { rotulo: 'Sem faixa de referência', cor: 'default', ordem: 0 }
};

/**
 * Cada entrada declara `cortes` em ordem crescente e a direção da leitura.
 *
 * `maiorMelhor` com cortes [a, b, c]: <a crítico, [a,b) atenção, [b,c) bom, >=c excelente.
 * `menorMelhor` com cortes [a, b, c]: <=a excelente, (a,b] bom, (b,c] atenção, >c crítico.
 */
export const CATALOGO_DE_FAIXAS = {
  liquidezCorrente: {
    direcao: 'maiorMelhor',
    cortes: [1.0, 1.2, 2.0],
    fonte: 'Faixa geral saudável 1,5–2,0; serviços operam saudáveis em 1,2–2,0 e indústria em 1,0–1,5 por imobilização em estoque.'
  },
  liquidezSeca: {
    direcao: 'maiorMelhor',
    cortes: [0.7, 1.0, 1.5],
    fonte: 'Convenção de análise contábil brasileira.'
  },
  liquidezImediata: {
    direcao: 'maiorMelhor',
    cortes: [0.1, 0.25, 0.5],
    fonte: 'Substitui o corte fixo de 0,30 antes aplicado a qualquer empresa.'
  },
  margemContribuicao: {
    direcao: 'maiorMelhor',
    cortes: [15, 30, 50],
    fonte: 'Sobre receita líquida. Comércio revendedor vive na faixa baixa por estrutura; serviços, na alta.'
  },
  margemLiquida: {
    direcao: 'maiorMelhor',
    cortes: [0, 5, 12],
    fonte: 'Sobre receita líquida.'
  },
  margemSeguranca: {
    direcao: 'maiorMelhor',
    cortes: [0, 10, 30],
    fonte: 'Análise custo-volume-lucro: quanto da venda pode ser perdido antes do prejuízo.'
  },
  icr: {
    direcao: 'maiorMelhor',
    cortes: [1.0, 2.0, 4.0],
    fonte: 'Acima de 2 já indica capacidade de pagamento dos juros; setores voláteis exigem mais.'
  },
  dscr: {
    direcao: 'maiorMelhor',
    cortes: [1.0, 1.3, 2.0],
    fonte: 'Convenção do produto — o painel já usava 1,30 como referência de conforto. Não vem de fonte externa.'
  },
  endividamento: {
    direcao: 'menorMelhor',
    cortes: [40, 60, 80],
    fonte: 'Passivo sobre ativo. Leitura conjunta obrigatória com liquidez: endividado e ilíquido é o sinal grave.'
  },
  composicaoEndividamento: {
    direcao: 'menorMelhor',
    cortes: [40, 50, 60],
    fonte: 'Convenção do produto para a fatia de curto prazo da dívida total.'
  },
  cicloFinanceiro: {
    direcao: 'menorMelhor',
    cortes: [0, 45, 90],
    fonte: 'Em dias. Varejo à vista com prazo de fornecedor opera com ciclo negativo saudável.'
  },
  runwayMeses: {
    direcao: 'maiorMelhor',
    cortes: [3, 6, 12],
    fonte: 'Só existe quando há queima real de caixa. Com lucro positivo o indicador não se aplica.'
  },
  fornecedorTop1Share: {
    direcao: 'menorMelhor',
    cortes: [15, 30, 50],
    fonte: 'Risco de concentração. Indústria de insumo único legitimamente opera alto.'
  },
  pessoalSobreReceita: {
    direcao: 'menorMelhor',
    cortes: [18, 30, 40],
    fonte: 'Folha sobre receita líquida, faixa de comércio e serviço leve. Não inclui retirada de sócios.'
  },
  custosFixosSobreReceita: {
    direcao: 'menorMelhor',
    cortes: [30, 40, 60],
    fonte: 'Convenção do produto, herdada do painel anterior.'
  }
};

/**
 * Em que degrau um valor cai. Nunca inventa: sem valor ou sem faixa declarada,
 * devolve o estado correspondente em vez de chutar um degrau.
 *
 * @returns {{nivel: string, rotulo: string, cor: string, fonte: string|null}}
 */
/**
 * Número que existe mas não deve ser julgado.
 *
 * Diferente de `ausente` (não temos o dado): aqui o dado está lá, e classificar
 * é que seria errado — porque a régua não se aplica àquele número. O resultado
 * de caixa é o caso: ele omite o CMV por construção, então uma faixa "excelente"
 * sobre ele diria que vai bem uma empresa cuja margem ignora o maior custo dela.
 */
export const semFaixaDeReferencia = (motivo = null) => ({
  nivel: 'semFaixa', ...NIVEIS.semFaixa, motivo, fonte: null
});

export const classificar = (chave, valor) => {
  const faixa = CATALOGO_DE_FAIXAS[chave];
  if (valor === null || valor === undefined || Number.isNaN(valor)) {
    return { nivel: 'ausente', ...NIVEIS.ausente, fonte: faixa?.fonte || null };
  }
  if (!faixa) {
    return { nivel: 'semFaixa', ...NIVEIS.semFaixa, fonte: null };
  }

  const [a, b, c] = faixa.cortes;
  let nivel;
  if (faixa.direcao === 'menorMelhor') {
    if (valor <= a) nivel = 'excelente';
    else if (valor <= b) nivel = 'bom';
    else if (valor <= c) nivel = 'atencao';
    else nivel = 'critico';
  } else {
    if (valor < a) nivel = 'critico';
    else if (valor < b) nivel = 'atencao';
    else if (valor < c) nivel = 'bom';
    else nivel = 'excelente';
  }

  return { nivel, ...NIVEIS[nivel], fonte: faixa.fonte };
};

/**
 * Saldo de tesouraria não tem corte numérico universal: a leitura é o SINAL
 * dele contra o tamanho da empresa, e a tendência quando há dois meses.
 *
 * Por isso ele não entra no catálogo de cortes — a faixa dele depende da
 * receita mensal, que é outro dado.
 */
export const classificarTesouraria = (saldo, receitaMensal) => {
  if (saldo === null || saldo === undefined || Number.isNaN(saldo)) {
    return { nivel: 'ausente', ...NIVEIS.ausente, fonte: null };
  }

  const fonte = 'Modelo Fleuriet, 2ª equação (T = CDG − NCG).';
  // Saldo negativo é veredito sem depender de receita: a empresa está
  // financiando o giro com dinheiro de curto prazo, e isso é crítico em
  // qualquer tamanho de empresa.
  if (saldo < 0) return { nivel: 'critico', ...NIVEIS.critico, fonte };

  // SEM RECEITA NÃO EXISTE "GRANDE O BASTANTE".
  //
  // O corte entre bom e excelente é o saldo contra um mês de faturamento. Sem
  // faturamento informado não há como dizer se R$ 8.000 de folga é um mês
  // inteiro de tranquilidade ou dois dias — e devolver "bom" era transformar a
  // FALTA de um dado em veredito de saúde positivo, exatamente o que este
  // arquivo faz certo três funções acima.
  if (!receitaMensal || receitaMensal <= 0) {
    return {
      nivel: 'semFaixa',
      ...NIVEIS.semFaixa,
      fonte: 'A faixa compara o saldo com um mês de faturamento, que não foi informado.'
    };
  }
  return saldo >= receitaMensal
    ? { nivel: 'excelente', ...NIVEIS.excelente, fonte }
    : { nivel: 'bom', ...NIVEIS.bom, fonte };
};
